import { DEFAULT_FREE_TRIAL_DAYS } from "@app/shared/constants";
import { MembershipBillingPlan } from "@app/shared/types";
import { Typography, Radio, alpha, Chip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classNames from "classnames";
import { CardToggle } from "components/CardToggle";
import {
    getCurrentCohort,
    isUserInFreeTrialSession,
    isUserOutsideOfMemberZone,
} from "features/signup/signupHelpers";
import { useLocation } from "react-router";

export interface DisplayDetails {
    title?: string;
    description: string;
}

export interface PricingCardProps {
    plan: MembershipBillingPlan;
    displayDetails: DisplayDetails;
    onSelect: (plan: MembershipBillingPlan) => void;
    isSelected: boolean;
}

const useStyles = makeStyles((theme) => ({
    freeTrialLabel: {
        position: "absolute",
        top: 0,
        left: 10,
        transform: "translateY(-50%)",
    },
    cardContent: {
        flex: 1,
        marginLeft: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            marginLeft: 0,
            marginTop: theme.spacing(1),
        },
    },
    priceContainer: {
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "row",
            alignItems: "center",
            gap: theme.spacing(1),
        },
    },
    noMarginBottom: {
        marginBottom: 0,
    },

    tightSpacing: {
        marginBottom: theme.spacing(-0.5),
        marginTop: 0,
        [theme.breakpoints.down("sm")]: {
            marginBottom: 0,
        },
    },
    contentContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
        },
    },
    cohortCard: {
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        border: theme.border.default,
        borderRadius: theme.borderRadius.default,
        marginBottom: theme.spacing(2.5),
        backgroundColor: alpha(theme.palette.aqua700, 0.15),
        borderColor: theme.palette.aqua700,
        boxShadow: `inset 0 0 0 1px ${theme.palette.aqua700}`,
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: theme.spacing(0),
        },
    },
    cohortPriceContainer: {
        display: "flex",
        flexDirection: "column",
    },
    cohortOldPriceLabel: {
        textDecoration: "line-through",
        marginTop: 0,
        marginBottom: 0,
        width: "max-content",
    },
    cohortFreeLabel: {
        marginTop: 0,
        marginBottom: 0,
        color: theme.palette.primary500,
    },
}));

export const PricingCard = (props: PricingCardProps) => {
    const { plan, displayDetails, onSelect, isSelected } = props;
    const classes = useStyles({ isSelected });

    const location = useLocation();

    const cohort = getCurrentCohort();
    const isFreeTrialSignupFlow = isUserInFreeTrialSession() && isUserOutsideOfMemberZone(location);
    const isCohortSponsored = cohort !== null;

    if (isCohortSponsored) {
        return (
            <div className={classes.cohortCard}>
                <div className={classes.cohortPriceContainer}>
                    <Typography variant="h5" className={classes.cohortOldPriceLabel}>
                        49 USD per month
                    </Typography>
                    <Typography variant="h5" className={classes.cohortFreeLabel}>
                        60 DAYS FREE
                    </Typography>
                </div>
                <Typography variant="body1" className={classes.noMarginBottom}>
                    {displayDetails.description}
                </Typography>
            </div>
        );
    } else {
        return (
            <CardToggle
                isSelected={isSelected}
                onClick={() => onSelect(plan)}
                radioValue={plan.baseAmount}
                testId="pricingCard"
            >
                {isFreeTrialSignupFlow && (
                    <Chip
                        variant="green"
                        label={`${DEFAULT_FREE_TRIAL_DAYS}-day free trial`}
                        className={classes.freeTrialLabel}
                        size="small"
                    />
                )}

                <div className={classes.contentContainer}>
                    <div className={classes.priceContainer}>
                        <Typography variant="h5" className={classes.tightSpacing}>
                            {plan.baseAmount} USD
                        </Typography>
                        <Typography variant="caption" className={classes.noMarginBottom}>
                            <strong>
                                {isFreeTrialSignupFlow ? "Monthly after trial" : "per month"}
                            </strong>
                        </Typography>
                    </div>

                    <div className={classes.cardContent}>
                        <Typography variant="body1" className={classes.noMarginBottom}>
                            <strong>{displayDetails.title}</strong>
                            {displayDetails.description}
                        </Typography>
                    </div>
                </div>
            </CardToggle>
        );
    }
};
